import { useUserStore } from "@/store/user";
import { createRandomString, jsonParse } from "@/utils";
import { useAES, encrypt, decrypt } from "@/utils/AES";
import type { GenerateResult } from "@/types/artwork/generate";
import { message } from "ant-design-vue";
import dayjs from "dayjs";
import { handleTransparentLayerWhite, getImageWaterMask } from "@/utils/image";
import { useArtworkStore } from "@/store/artwork";
import type { ImageCommonState } from "@/types/image";
import { cloneDeep } from "lodash";
import { usePayStore } from "@/store/pay";
import { checkDateChange } from "@/utils/dateChecker";
import { TEAM_QUOTA_EXCEEDED_CODE, TEAM_QUOTA_EXCEEDED_TITLE, TEAM_QUOTA_EXCEEDED_CONTENT, shouldShowTeamQuotaExceededModal } from "@/utils/teamQuotaWarning";
import { Modal } from "ant-design-vue";
import { h } from "vue";
import SvgIcon from "@/components/SvgIcon/index.vue";
import { newUserFirstGenerate } from "@/views/workspace/service";
import { useNewUserGuide } from "@/store/newUserGuide";
import { useNewUserGuideAbTest } from "../abTest/useNewUserGuideAbTest";
import { logger } from "@/utils/logger";
import { reportSupportLastError } from "@/hooks/supportWidgetContext";

export const initGenerateResult = {
  data: null,
  msg: "",
  progress: 0,
  code: -1,
  queueNum: 0,
  queueTotalNum: 0,
  startTime: 0
} as const;

// 移除无效字段
function removeInvalidField(data: object, validFields: string[]) {
  if (data) {
    const tempObj: any = {};

    for (let key in data) {
      if (validFields.includes(key)) {
        tempObj[key] = data[key as keyof typeof data];
      }
    }

    return tempObj;
  }
}

// 移除图片对象中的无效字段
const imageValidField = ["id", "width", "height", "image", "weight"];
function getReducedPictureField(images: ImageCommonState | ImageCommonState[]) {
  if (Array.isArray(images)) {
    return images.map((item) => removeInvalidField(item, imageValidField));
  }
  return removeInvalidField(images, imageValidField);
}

// 移除模型中的无效字段
const modelValidField = [
  "id",
  "modelName",
  "preview",
  "ckptType",
  "loraType",
  "strength",
  "source",
  "teamId",
  "shareType",
  "parentId",
];

// 不需要传 imageGuidanceWeights 的 ckptType 类型
const NO_IMAGE_GUIDANCE_TYPES = ["SDXL", "A1", "Animagine", "Pony"];

function getReducedModelField(modelList: []) {
  if (Array.isArray(modelList)) {
    return modelList.map((item) => removeInvalidField(item, modelValidField));
  }
  return [];
}

// 预加载生成的图
async function preLoadImages(images: ImageCommonState[] = []): Promise<void> {
  logger.info('开始预加载图片', {
    imagesCount: images.length
  });

  const startTime = Date.now();
  await Promise.all(
    images.map(async (item: any, index: number) => {
      await new Promise<void>((resolve) => {
        const img = new Image();
        img.crossOrigin = "";
        img.src = getImageWaterMask(item.image);

        img.onload = () => {
          logger.debug(`图片预加载成功 ${index + 1}/${images.length}`);
          resolve();
        };

        img.onerror = () => {
          logger.warn(`图片预加载失败 ${index + 1}/${images.length}`, {
            imageId: item.id
          });
          resolve();
        };
      });
    })
  );

  logger.info('所有图片预加载完成', {
    duration: Date.now() - startTime,
    imagesCount: images.length
  });
}

export function getClientId() {
  const userStore = useUserStore();
  let clientId = userStore.userInfo.id + "-" + createRandomString();

  // 除 production 环境外，生图不加密
  if (!useAES) {
    clientId += "-test";
  }
  return clientId;
}

async function getParams(
  params: any = {},
  { transparentLayerWhite = false, isReconnection = false }
) {
  const userStore = useUserStore();

  logger.info('开始处理参数', {
    transparentLayerWhite,
    isReconnection
  });

  if (
    Object.prototype.toString.call(params) === "[object Object]" &&
    transparentLayerWhite
  ) {
    logger.debug('处理透明图层');
    const layoutReferId = await handleTransparentLayerWhite(
      params.layoutRefer?.image
    );

    if (Number(layoutReferId)) {
      params.layoutReferId = layoutReferId;
      logger.debug('透明图层处理完成', { layoutReferId });
    }
  }

  if (Object.prototype.toString.call(params) === "[object Object]") {
    params.userId = userStore.userInfo.id;

    // 断线重连标识
    if (isReconnection) {
      params.sendType = "doReConnect";
      logger.debug('设置断线重连标识');
    } else if (params.sendType !== "doReConnect") {
      // 是否使用加速通道
      const artworkStore = useArtworkStore();
      params.vipChannel = artworkStore.channel === "expedite"; // normal
      logger.debug('设置通道类型', {
        channel: artworkStore.channel
      });
    }
  }

  // 移除参考图、形象样式参考图中多余字段
  if (params.layoutRefer) {
    params.layoutRefer = getReducedPictureField(params.layoutRefer);
    logger.debug('处理参考图字段');
  }
  if (params.styleReferList) {
    params.styleReferList = getReducedPictureField(params.styleReferList);
    logger.debug('处理样式参考图字段');
  }

  // 移除模型中多余字段
  if (params.modelList) {
    params.modelList = getReducedModelField(params.modelList);
    logger.debug('处理模型字段');
  }

  const result = useAES ? encrypt(params) : JSON.stringify(params);
  logger.info('参数处理完成', {
    isEncrypted: !!useAES
  });

  return result;
}

interface Options {
  onWebSocketInited?: (
    websocket: WebSocket,
    clientId: string,
    isReconnection: boolean
  ) => void;
  onUpdate?: (messageData: GenerateResult) => void;
  onGenerating?: (messageData: GenerateResult) => void;
  onSuccess?: (messageData: GenerateResult) => void;
  onError?: (messageData: GenerateResult) => void;
  onFinish?: () => void;
  onClose?: () => void;
  useToast?: boolean;
  transparentLayerWhite?: boolean;
  usePreLoadImages?: boolean;
}

export async function useGenerate(
  params: any,
  {
    onWebSocketInited,
    onUpdate,
    onGenerating,
    onSuccess,
    onError,
    onFinish,
    onClose,
    useToast = true,
    transparentLayerWhite = false,
    usePreLoadImages = true,
  }: Options
) {
  let clientId = params?.clientId || getClientId();
  delete params?.clientId;

  const userStore = useUserStore();
  // const { isTaskQueue2User } = storeToRefs(userStore)

  let startTime = new Date().getTime();
  let startQueueTime: any = null; // 开始排队的时间
  let endQueueTime: any = null; // 结束排队的时间
  let oldQueueNum: number; // 上一次排队位置

  let emitPowerUpdated = false; // 是否已经在此次生图更新算力
  let reconnectionStartTime: number; // 断线重连开始时间
  let isTaskCompleted = false; // 任务是否已完成，完成后不再触发重连
  let isReconnecting = false; // 是否正在重连中，防止重入


  // 【AI创作】SDXL、A1、Animagine、Pony，生图参数不需要传imageGuidanceWeights，只有V1才会传
  if (params?.modelList && Array.isArray(params.modelList)) {
    // 只要有一个模型是指定类型，就不需要 imageGuidanceWeights
    const shouldRemoveGuidance = params.modelList.some((item: any) => 
      NO_IMAGE_GUIDANCE_TYPES.some(type => item.ckptType?.includes(type))
    );
    
    if (shouldRemoveGuidance) {
      delete params.imageGuidanceWeights;
    }
  }

  // 日志：生图流程开始
  logger.info('开始生图', {
    clientId,
    startTime: new Date(startTime).toISOString()
  });

  /**
   * 心跳检测
   */
  const HEARTBEAT_TIMEOUT = 45000; // 45秒超时（大于后端30秒间隔）
  let heartbeatCheckTimer: NodeJS.Timeout;
  let lastHeartbeatTime = Date.now();

  let stringParams = await getParams(params, {
    transparentLayerWhite,
  });

  // 日志：参数处理完成
  logger.debug('参数处理完成', {
    clientId
  });

  const newUserGuideStore = useNewUserGuide();
  const { abType } = useNewUserGuideAbTest();
  if (newUserGuideStore.isMockGenerate) {
    // 日志：新用户引导模拟生图
    logger.info('新用户引导模拟生图', { clientId });
    let res = await newUserFirstGenerate({
      clientId,
      param: stringParams,
      artworkId: params.artworkId,
      abTest: abType.value,
      modelId: params.modelList[0].id,
    });
      newUserGuideStore.step3ForB(res);

    // if(isBUser.value){
    // }else{
    //   newUserGuideStore.showStep4(res);
    // }

    return false;
  }

  const artworkStore = useArtworkStore();
  if (artworkStore.channel !== "expedite") {
    userStore.optimisticallyConsumeActivityRight(params?.module, clientId);
  }

  // 日志：创建WebSocket连接
  logger.info('创建WebSocket连接', {
    clientId
  });

  let socket = new WebSocket(
    `${import.meta.env.VITE_GLOB_WS_BASEURL}/websocket/${clientId}`
  );

  // 初始化完成
  typeof onWebSocketInited === "function" &&
    onWebSocketInited(socket, clientId, false);
  typeof onUpdate === "function" &&
    onUpdate({
      ...cloneDeep(initGenerateResult),
      code: 0,
      msg: "等待处理中...",
    });

  socketListener(socket);

  // socket 监听
  function socketListener(socket: WebSocket) {
    socket.addEventListener("open", () => {
      // 日志：WebSocket连接成功
      logger.info('WebSocket连接成功', {
        clientId,
        connectTime: new Date().toISOString()
      });

      lastHeartbeatTime = Date.now();

      heartbeatCheckTimer = setInterval(() => {
        const timeDiff = Date.now() - lastHeartbeatTime;

        if (timeDiff > HEARTBEAT_TIMEOUT) {
          console.error("心跳超时触发重连");
          // 日志：心跳超时
          logger.warn('心跳超时触发重连', {
            clientId,
            duration: timeDiff
          });
          socket.close(4001, "Heartbeat timeout");
          handleReconnection();
          clearInterval(heartbeatCheckTimer);
        }
      }, 5000);

      // 重置断线重连开始时间
      if (reconnectionStartTime) {
        reconnectionStartTime = 0;
      }

      isReconnecting = false;

      // 日志：发送生图参数
      logger.debug('发送生图参数', {
        clientId
      });
      socket.send(stringParams);
      checkDateChange();
    });

    socket.addEventListener("message", (event: any) => {
      const message = event.data || "";

      /**
       * 心跳
       */
      if (message === "heartBeat") {
        lastHeartbeatTime = Date.now();
        return;
      }

      // 日志：收到WebSocket消息
      logger.debug('收到WebSocket消息', {
        clientId
      });

      const data = useAES ? decrypt(message) : message;

      handleSocketMessage(data);
    });

    socket.addEventListener("error", (event: any) => {
      console.log("websocket error:", event);
      // 日志：WebSocket错误
      logger.error('WebSocket错误', {
        clientId,
        error: event,
        duration: Date.now() - startTime
      });
      socket.close();
    });

    socket.addEventListener("close", (event: any) => {
      console.log("websocket close:", event);
      // 日志：WebSocket关闭
      logger.info('WebSocket关闭', {
        clientId,
        duration: Date.now() - startTime,
        closeCode: event.code
      });

      clearInterval(heartbeatCheckTimer);

      if (isTaskCompleted) {
        return;
      }

      if ([1001, 1006].includes(event.code)) {
        handleReconnection();
      } else if ([1009].includes(event.code)) {
        handleError('连接异常');
      }

      typeof onClose === "function" && onClose();
    });
  }

  // 处理生图异常
  function handleError(msg: string, data: object = {}) {
    isTaskCompleted = true;
    userStore.rollbackActivityRightDeduction(clientId);

    reportSupportLastError({
      code: (data as { code?: number }).code ?? -1,
      message: msg
    });

    // 日志：生图错误
    logger.error('生图错误', {
      clientId,
      duration: Date.now() - startTime,
      errorMsg: msg
    });

    useToast && message.error(msg);
    typeof onUpdate === "function" &&
      onUpdate({
        data: null,
        msg,
        progress: 0,
        code: -1,
        ...data,
      });

    typeof onError === "function" &&
      onError({
        data: null,
        msg,
        progress: 0,
        code: -1,
        ...data,
        vipChannel: params.vipChannel,
      });
    typeof onFinish === "function" && onFinish();

    socket.close();
  }

  // 处理断线重连
  function handleReconnection() {
    if (isTaskCompleted || isReconnecting) {
      return;
    }

    console.log(socket);

    // 日志：开始断线重连处理
    logger.info('开始断线重连处理', {
      clientId,
      duration: Date.now() - startTime
    });

    if (socket.readyState === WebSocket.CLOSED) {
      console.log("开始断线重连");

      isReconnecting = true;

      // 设置重连开始时间
      if (!reconnectionStartTime) {
        reconnectionStartTime = new Date().getTime();
        // 日志：设置重连开始时间
        logger.debug('设置重连开始时间', {
          clientId
        });
      }

      const diffInMinutes = dayjs().diff(
        dayjs(reconnectionStartTime),
        "minute"
      );

      // 日志：检查重连时间
      logger.debug('检查重连时间', {
        clientId,
        duration: Date.now() - reconnectionStartTime,
        diffInMinutes
      });

      // 重连超过 6 分钟提示生图失败
      if (diffInMinutes > 6) {
        // 日志：重连超时
        logger.error('重连超时，生图失败', {
          clientId,
          duration: Date.now() - startTime,
          diffInMinutes
        });
        return handleError("生图失败，请重试", {
          client_id: clientId,
          loading_time_number: parseInt(
            (dayjs().diff(dayjs(startTime)) / 1000).toFixed(0)
          ),
        });
      }

      setTimeout(async () => {
        // 日志：执行重连
        logger.info('执行重连', {
          clientId,
          duration: Date.now() - startTime
        });

        stringParams = await getParams(params, {
          transparentLayerWhite,
          isReconnection: true,
        });

        socket = new WebSocket(
          `${import.meta.env.VITE_GLOB_WS_BASEURL}/websocket/${clientId}`
        );

        typeof onWebSocketInited === "function" &&
          onWebSocketInited(socket, clientId, true);

        socketListener(socket);
      }, 3000);
    }
  }

  // 处理生图消息
  async function handleSocketMessage(messageData: any) {
    // 日志：开始处理消息
    logger.debug('开始处理消息', {
      clientId
    });

    if (
      jsonParse(messageData) &&
      typeof jsonParse(messageData).status !== "undefined"
    ) {
      if (!emitPowerUpdated) {
        emitPowerUpdated = true;
        // 日志：首次更新算力
        logger.debug('首次更新算力', { clientId });
        // 通知更新算力
        userStore.queryUserRights();
      }

      const data = jsonParse(messageData);
      let { status, queueNum, queueTotalNum, errMsg } = data;

      // 日志：解析消息数据
      logger.debug('解析消息数据', {
        clientId,
        status
      });

      // 会员相关异常常见先更新用户当前权益信息
      if (Number(status) && ![1, 2, 3, -2, -3].includes(status)) {
        // 日志：异常状态，更新用户权益
        logger.warn('异常状态，更新用户权益', {
          clientId,
          status
        });
        await userStore.queryUserRights();
      }

      const payStore = usePayStore();

      payStore.ballParams.button = "button_creation_img_concurrent";

      switch (status) {
        case 1: // 排队中
          if (!startQueueTime) {
            startQueueTime = new Date().getTime();
            // 日志：开始排队
            logger.info('开始排队', {
              clientId,
              queueNum,
              queueTotalNum
            });
          }

          // 被会员用户插队时，让当前用户无感
          if (oldQueueNum !== undefined && oldQueueNum < queueNum) {
            queueNum = oldQueueNum;
          } else {
            oldQueueNum = queueNum;
          }

          // 日志：排队状态更新
          logger.debug('排队状态更新', {
            clientId,
            queueNum
          });

          typeof onUpdate === "function" &&
            onUpdate({
              data: null,
              msg: "正在排队中",
              progress: 0,
              code: 1,
              queueNum,
              queueTotalNum,
            });
          break;
        case 2: // 生成中
          endQueueTime = new Date().getTime();
          // 日志：开始生成
          logger.info('开始生成', {
            clientId,
            duration: endQueueTime - startTime
          });

          typeof onUpdate === "function" &&
            onUpdate({
              data: null,
              msg: "正在快速生成中",
              progress: 0,
              code: 2,
            });

          typeof onGenerating === "function" &&
            onGenerating({
              data: null,
              msg: "正在快速生成中",
              progress: 0,
              code: 2,
            });
          break;
        case 3: // 已完成
          isTaskCompleted = true;
          const completeTime = new Date().getTime();
          // 日志：生成完成
          logger.info('生成完成', {
            clientId,
            duration: completeTime - startTime,
            imagesCount: data.images?.length || 0
          });

          typeof onUpdate === "function" &&
            onUpdate({
              data,
              msg: "seccess",
              progress: 100,
              code: 3,
              client_id: clientId,
              queue_time_number: startQueueTime
                ? parseInt(
                  (
                    dayjs(endQueueTime).diff(dayjs(startQueueTime)) / 1000
                  ).toFixed(0)
                )
                : 0,
              loading_time_number: parseInt(
                (dayjs().diff(dayjs(startTime)) / 1000).toFixed(0)
              ),
            });

          typeof onSuccess === "function" &&
            onSuccess({
              data,
              msg: "seccess",
              progress: 100,
              code: 3,
              client_id: clientId,
              queue_time_number: startQueueTime
                ? parseInt(
                  (
                    dayjs(endQueueTime).diff(dayjs(startQueueTime)) / 1000
                  ).toFixed(0)
                )
                : 0,
              loading_time_number: parseInt(
                (dayjs().diff(dayjs(startTime)) / 1000).toFixed(0)
              ),
              vipChannel: params.vipChannel,
            });
          
          if (usePreLoadImages) {
            logger.debug('开始预加载图片', { clientId });
            void preLoadImages(data.images).then(() => {
              logger.debug('图片预加载完成', {
                clientId,
                duration: Date.now() - completeTime
              });
            });
          }

          // 生图成功后刷新免费次数（活动权益）
          userStore.finalizeActivityRightDeduction(clientId);
          userStore.queryActivityRights();
          
          typeof onFinish === "function" && onFinish();
          socket.close();
          break;
        case -1:
          // 日志：一般错误
          logger.error('一般错误', {
            clientId,
            duration: Date.now() - startTime,
            errorMsg: errMsg || "生图失败，请重试"
          });
          handleError(errMsg || "生图失败，请重试", {
            client_id: clientId,
            loading_time_number: parseInt(
              (dayjs().diff(dayjs(startTime)) / 1000).toFixed(0)
            ),
          });
          break;
        case -2:
          // 日志：敏感词错误
          logger.error('敏感词错误', {
            clientId,
            duration: Date.now() - startTime
          });
          handleError("生图失败，提示词含有敏感词");
          break;
        case -3:
          // 日志：登录失效错误
          logger.error('登录失效错误', {
            clientId,
            duration: Date.now() - startTime
          });
          handleError("生图失败，登录已失效");
          break;
        case -100:
          // 日志：算力不足错误
          logger.error('算力不足错误', {
            clientId,
            duration: Date.now() - startTime,
            highestPackage: userStore.highestPackage,
            vipChannel: params.vipChannel
          });
          if (userStore.hasMembershipAtLeast("svip") && params.vipChannel) {
            handleError("生图失败，您的会员加速算力已不足");
          } else {
            handleError("生图失败，您的算力已不足");
          }

          payStore.ballParams.button = "button_creation_img_not_points";
          userStore.usePayModal({ freeToast: false, powerPackage: true });
          break;
        case -200:
          // 日志：并发任务错误
          logger.error('并发任务错误', {
            clientId,
            duration: Date.now() - startTime
          });
          handleError("生图失败，当前已有任务进行中，请稍后提交");
          payStore.ballParams.button = "button_creation_img_concurrent";
          // handleError 已 toast，避免免费用户再弹「需升级为会员」重复提示
          userStore.usePayModal({ freeToast: false });
          break;
        case -201:
          // 日志：套餐权益错误
          logger.error('套餐权益错误', {
            clientId,
            duration: Date.now() - startTime,
            showVip: userStore.showVip
          });
          handleError(
            userStore.showVip
              ? "生图失败，生图数量不符合当前套餐权益"
              : "生图失败"
          );
          userStore.usePayModal({ freeToast: false });
          break;
        case -202:
          // 日志：VIP权益错误
          logger.error('VIP权益错误', {
            clientId,
            duration: Date.now() - startTime,
            showVip: userStore.showVip
          });
          handleError(
            userStore.showVip ? "生图失败，倍数下载需vip权益" : "生图失败"
          );
          userStore.usePayModal({ freeToast: false });
          break;
        case -203:
          // 日志：会员权益使用错误
          logger.error('会员权益使用错误', {
            clientId,
            duration: Date.now() - startTime,
            showVip: userStore.showVip
          });
          handleError(
            userStore.showVip ? "生图失败，您不能使用会员权益" : "生图失败"
          );
          userStore.usePayModal({ freeToast: false });
          break;
        case -999:
          // 日志：服务器要求重连
          logger.info('服务器要求重连', {
            clientId,
            duration: Date.now() - startTime
          });
          socket.close();
          handleReconnection();
          break;
        case TEAM_QUOTA_EXCEEDED_CODE:
          logger.error('团队个人额度已用尽', { clientId, duration: Date.now() - startTime });
          isTaskCompleted = true;
          userStore.rollbackActivityRightDeduction(clientId);
          reportSupportLastError({ code: TEAM_QUOTA_EXCEEDED_CODE, message: TEAM_QUOTA_EXCEEDED_CONTENT });
          if (shouldShowTeamQuotaExceededModal()) {
            Modal.warning({
              title: TEAM_QUOTA_EXCEEDED_TITLE,
              icon: h(SvgIcon, { directory: 'others', name: 'tip-modal-icon', size: 24, class: 'anticon' }),
              content: TEAM_QUOTA_EXCEEDED_CONTENT,
              okText: '我知道了',
              okType: 'primary',
              wrapClassName: 'custom-tip-modal',
              bodyStyle: { padding: '16px' },
              centered: true,
            });
          }
          typeof onError === "function" &&
            onError({
              data: null,
              msg: TEAM_QUOTA_EXCEEDED_CONTENT,
              progress: 0,
              code: TEAM_QUOTA_EXCEEDED_CODE,
              client_id: clientId,
              vipChannel: params.vipChannel,
            });
          typeof onFinish === "function" && onFinish();
          socket.close();
          break;
        default: // 断线重连
          // 日志：未知状态，触发断线重连
          logger.warn('未知状态，触发断线重连', {
            clientId,
            duration: Date.now() - startTime,
            status
          });
          socket.close();
          break;
      }
    }
  }
}
